import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  MenuItem,
  Select,
  InputAdornment,
  InputLabel,
  Switch,
  Card,
  CardContent,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import {
  Info as InfoIcon,
  Percent as PercentIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

interface InvoiceData {
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  client: string;
  clientId: string;
  clientDetails: {
    address: string;
    gstin: string;
    email: string;
    phone: string;
    state: string;
  };
  items: any[];
  subtotal: number;
  tax: number;
  discount: number;
  amount: number;
  notes: string;
  termsAndConditions: string;
  taxType: string;
  taxRate: number;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  taxable: boolean;
  userId: string;
  createdAt: string;
}

interface ChargesDiscountsStepProps {
  invoiceData: InvoiceData;
  updateInvoiceData: (data: Partial<InvoiceData>) => void;
}

// Additional charge item interface
interface AdditionalCharge {
  id: number;
  name: string;
  amount: number;
}

const ChargesDiscountsStep: React.FC<ChargesDiscountsStepProps> = ({
  invoiceData,
  updateInvoiceData
}) => {
  // State for discount settings
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>(invoiceData.discountType || 'percentage');
  const [discountValue, setDiscountValue] = useState(invoiceData.discountValue || 0);
  const [discountAmount, setDiscountAmount] = useState(invoiceData.discount || 0);
  
  // State for tax settings
  const [taxRate, setTaxRate] = useState(invoiceData.taxRate || 18);
  const [taxable, setTaxable] = useState(invoiceData.taxable !== undefined ? invoiceData.taxable : true);
  const [taxAmount, setTaxAmount] = useState(invoiceData.tax || 0);
  
  // State for additional charges
  const [additionalCharges, setAdditionalCharges] = useState<AdditionalCharge[]>([]);
  const [newChargeName, setNewChargeName] = useState('');
  const [newChargeAmount, setNewChargeAmount] = useState(0);
  const [totalAdditionalCharges, setTotalAdditionalCharges] = useState(0);
  
  // State for notes and terms
  const [notes, setNotes] = useState(invoiceData.notes || '');
  const [termsAndConditions, setTermsAndConditions] = useState(invoiceData.termsAndConditions || '');
  
  // Calculated totals
  const [subtotal, setSubtotal] = useState(invoiceData.subtotal || 0);
  const [total, setTotal] = useState(invoiceData.amount || 0);
  
  // Calculate totals when items or discount changes
  useEffect(() => {
    if (!invoiceData.items) return;
    
    // Calculate subtotal
    const calculatedSubtotal = invoiceData.items.reduce(
      (total: number, item: any) => total + (item.amount || 0),
      0
    );
    
    // Calculate discount amount
    let calculatedDiscountAmount = 0;
    if (discountType === 'percentage') {
      calculatedDiscountAmount = calculatedSubtotal * (discountValue / 100);
    } else {
      calculatedDiscountAmount = discountValue;
    }
    
    // Ensure discount doesn't exceed subtotal
    calculatedDiscountAmount = Math.min(calculatedDiscountAmount, calculatedSubtotal);
    
    // Calculate taxable amount (post-discount)
    const taxableAmount = calculatedSubtotal - calculatedDiscountAmount;
    
    // Calculate total additional charges
    const additionalChargesSum = additionalCharges.reduce((sum, charge) => sum + charge.amount, 0);
    
    // Calculate total - tax is now calculated at the item level
    const calculatedTotal = taxableAmount + additionalChargesSum;
    
    // Update state
    setSubtotal(calculatedSubtotal);
    setDiscountAmount(calculatedDiscountAmount);
    setTotalAdditionalCharges(additionalChargesSum);
    setTotal(calculatedTotal);
    
    // Update invoice data
    updateInvoiceData({
      subtotal: calculatedSubtotal,
      discount: calculatedDiscountAmount,
      amount: calculatedTotal,
      discountType,
      discountValue,
      taxRate,
      taxable,
      notes,
      termsAndConditions
    });
  }, [
    invoiceData.items, 
    discountType, 
    discountValue, 
    taxRate, 
    taxable, 
    additionalCharges,
    notes,
    termsAndConditions
  ]);
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };
  
  // Handle discount type change
  const handleDiscountTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountType(event.target.value as 'percentage' | 'fixed');
    // Reset discount value when changing type
    setDiscountValue(0);
  };
  
  // Handle discount value change
  const handleDiscountValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (isNaN(value)) {
      setDiscountValue(0);
      return;
    }
    
    // Ensure discount doesn't exceed 100% or subtotal
    if (discountType === 'percentage') {
      setDiscountValue(Math.min(value, 100));
    } else {
      setDiscountValue(Math.min(value, subtotal));
    }
  };
  
  // Handle tax rate change
  const handleTaxRateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (isNaN(value)) {
      setTaxRate(0);
      return;
    }
    
    // Ensure tax rate is between 0 and 100
    setTaxRate(Math.min(Math.max(value, 0), 100));
  };
  
  // Handle taxable toggle
  const handleTaxableChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTaxable(event.target.checked);
  };
  
  // Handle adding a new charge
  const handleAddCharge = () => {
    if (newChargeName.trim() === '' || newChargeAmount <= 0) return;
    
    const newCharge: AdditionalCharge = {
      id: Date.now(),
      name: newChargeName.trim(),
      amount: newChargeAmount
    };
    
    setAdditionalCharges([...additionalCharges, newCharge]);
    
    // Reset form
    setNewChargeName('');
    setNewChargeAmount(0);
  };
  
  // Handle removing a charge
  const handleRemoveCharge = (id: number) => {
    setAdditionalCharges(additionalCharges.filter(charge => charge.id !== id));
  };
  
  // Handle notes change
  const handleNotesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(event.target.value);
  };
  
  // Handle terms and conditions change
  const handleTermsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAndConditions(event.target.value);
  };
  
  return (
    <Box>
      <Grid container spacing={3}>
        {/* Discounts Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Discount
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={discountType}
                    onChange={handleDiscountTypeChange}
                    name="discount-type"
                  >
                    <FormControlLabel 
                      value="percentage" 
                      control={<Radio />} 
                      label="Percentage" 
                    />
                    <FormControlLabel 
                      value="fixed" 
                      control={<Radio />} 
                      label="Fixed Amount" 
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
                  type="number"
                  value={discountValue}
                  onChange={handleDiscountValueChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {discountType === 'percentage' ? <PercentIcon /> : <CurrencyRupeeIcon />}
                      </InputAdornment>
                    ),
                    inputProps: { min: 0, max: discountType === 'percentage' ? 100 : subtotal }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Discount Amount:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(discountAmount)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Additional Charges Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Additional Charges
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Charge Name"
                  value={newChargeName}
                  onChange={(e) => setNewChargeName(e.target.value)}
                  placeholder="E.g., Shipping, Packaging, etc."
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={newChargeAmount}
                  onChange={(e) => setNewChargeAmount(parseFloat(e.target.value) || 0)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CurrencyRupeeIcon />
                      </InputAdornment>
                    ),
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddCharge}
                  disabled={newChargeName.trim() === '' || newChargeAmount <= 0}
                  sx={{ borderRadius: 2 }}
                >
                  Add Charge
                </Button>
              </Grid>
              
              <Grid item xs={12}>
                {additionalCharges.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Charge Name</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {additionalCharges.map((charge) => (
                          <TableRow key={charge.id}>
                            <TableCell component="th" scope="row">
                              {charge.name}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(charge.amount)}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveCharge(charge.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            Total Additional Charges
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totalAdditionalCharges)}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No additional charges added yet
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Notes and Terms Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Notes & Terms
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={notes}
                  onChange={handleNotesChange}
                  placeholder="Add notes that will be displayed on the invoice..."
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Terms & Conditions"
                  multiline
                  rows={3}
                  value={termsAndConditions}
                  onChange={handleTermsChange}
                  placeholder="Add your terms and conditions..."
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        
        {/* Invoice Summary Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Invoice Summary
            </Typography>
            
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body1">Subtotal:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body1">{formatCurrency(subtotal)}</Typography>
              </Grid>
              
              {discountAmount > 0 && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body1">Discount:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1" color="error">
                      - {formatCurrency(discountAmount)}
                    </Typography>
                  </Grid>
                </>
              )}
              
              {totalAdditionalCharges > 0 && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body1">Additional Charges:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1">{formatCurrency(totalAdditionalCharges)}</Typography>
                  </Grid>
                </>
              )}
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              
              <Grid item xs={6}>
                <Typography variant="h6" fontWeight="bold">Total:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {formatCurrency(total)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChargesDiscountsStep; 